import React, { useState, useEffect } from 'react';
import { Card, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import paginationFactory, { PaginationProvider, PaginationListStandalone, SizePerPageDropdownStandalone } from 'react-bootstrap-table2-paginator';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Fetcher } from '../../Helpers/fetcher.js';
import { Notify } from '../../Helpers/notification.js';

const CliUsage = ({ app }) => {
    const today = new Date().toISOString().split('T')[0];
    const [data, setData] = useState([]);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [noDataDisplayMsg, setNoDataDisplayMsg] = useState('No data available');

    const fetchUserStatistics = async () => {
        setLoading(true);

        const payload = {
            startDate,
            endDate,
        };

        try {
            const res = await Fetcher.post(`${app.apiPath}/api/obd/total/cli/usage`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            const dataArray = Array.isArray(res) ? res : [];
            setData(dataArray);

            if (!dataArray.length) {
                setNoDataDisplayMsg('No data available for the selected date range');
            }
        } catch (err) {
            console.error('Error fetching User Statistics:', err);
            Notify.notify('Error fetching data');
            setNoDataDisplayMsg('Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (setter) => (date) => {
        setter(date ? date.toISOString().split('T')[0] : '');
    };

    const submitSearch = (e) => {
        e.preventDefault();
        fetchUserStatistics();
    };

    const columns = [
        { dataField: 'id', text: 'ID', sort: true },
        { dataField: 'cli', text: 'CLI', sort: true },
        { dataField: 'requestDate', text: 'Request Date', sort: true },
        { dataField: 'totalNos', text: 'Total Nos', sort: true },
        { dataField: 'nosProcessed', text: 'Processed Nos', sort: true },
        {
            dataField: 'dndCount',
            text: 'DND Count',
            sort: true,
            formatter: (cell) => cell ? Number(cell) : 0
        },
        {
            text: 'Pending',
            formatter: (_, row) => {
                const total = Number(row.totalNos) || 0;
                const processed = Number(row.nosProcessed) || 0;
                const dnd = Number(row.dndCount) || 0;
                return total - processed - dnd;
            }
        }
    ];

    const paginationOptions = {
        custom: true,
        totalSize: data.length,
        sizePerPage: 5,
        sizePerPageList: [5]
    };

    useEffect(() => {
        fetchUserStatistics();
    }, []);

    const { SearchBar, ClearSearchButton } = Search;


    return (
        <Col sm={12}>
            <Card>
                <Card.Header>CLI Usage Summary</Card.Header>
                <Card.Body>

                    <Form className="mb-3" onSubmit={submitSearch}>
                        <Row className="align-items-end">
                            <Col sm={3}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker
                                        className="form-control"
                                        dateFormat="yyyy-MM-dd"
                                        selected={startDate ? new Date(startDate) : null}
                                        onChange={handleDateChange(setStartDate)}
                                        placeholderText="Start Date"
                                        minDate={new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)}  // only last 3 days
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={3}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <DatePicker
                                        className="form-control"
                                        dateFormat="yyyy-MM-dd"
                                        selected={endDate ? new Date(endDate) : null}
                                        onChange={handleDateChange(setEndDate)}
                                        placeholderText="End Date"
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={2}>
                                <Button variant="primary" type="submit">Search</Button>
                                <Button variant="link" className="p-0 ms-2 text-decoration-none"
                                    onClick={() => {
                                        setStartDate(today);
                                        setEndDate(today);
                                    }}
                                >
                                    Clear
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {loading ? (
                        <div className="my-5 text-center"><Spinner /></div>
                    ) : (
                        <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                            {({ paginationProps, paginationTableProps }) => (
                                <ToolkitProvider keyField="userId" data={data} columns={columns} search bootstrap4>
                                    {props => (
                                        <div>
                                            <Row className="align-items-center mb-3">
                                                <Col md="auto">
                                                    Show <SizePerPageDropdownStandalone {...paginationProps} /> items
                                                </Col>
                                                <Col md="auto">
                                                    <SearchBar {...props.searchProps} />
                                                </Col>
                                                <Col md="auto">
                                                    <ClearSearchButton {...props.searchProps} className="btn-primary" />
                                                </Col>
                                            </Row>

                                            <BootstrapTable
                                                bordered
                                                striped
                                                condensed
                                                wrapperClasses="table-responsive"
                                                {...props.baseProps}
                                                {...paginationTableProps}
                                                noDataIndication={<div>{noDataDisplayMsg}</div>}
                                            />

                                            <PaginationListStandalone {...paginationProps} />
                                        </div>
                                    )}
                                </ToolkitProvider>
                            )}
                        </PaginationProvider>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );
};

export default CliUsage;
